import { useState, useEffect, useCallback } from "react";
import { usePageBack } from "../context/PageBackContext.js";

import { api } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useAuth } from "../hooks/useAuth.js";
import { AsyncButton } from "../components/ui/async-button.js";
import { SelectField } from "../components/ui/select-field.js";
import { ADMIN_ROLES, ASSIGNABLE_ROLES } from "@hrms/types";

// ─── Types ────────────────────────────────────────────────────────────────────

interface FieldDef {
  field: string; label: string; section: string;
  sensitivity: string; maskPattern: string | null; isActive: boolean;
}

interface RolePermission {
  role: string; field: string;
  canViewInList: string; canViewInProfile: string; canEdit: string;
  approverRoles: string[]; lockedWhenStatus: string[];
}

interface PermMatrix {
  fields: FieldDef[];
  permissions: RolePermission[];
}

interface TempGrant {
  id: string; field: string; canViewInList: string; canViewInProfile: string; canEdit: string;
  reason: string; expiresAt: string; revoked: boolean;
  user: { id: string; email: string; employee: { firstName: string; lastName: string } | null };
  grantedBy: { email: string };
}

interface UserOption { id: string; email: string; employee: { firstName: string; lastName: string } | null }

interface ModuleDef { key: string; label: string; routePrefix: string; isActive: boolean }
interface ModuleAccessRow { role: string; moduleKey: string; canView: boolean }
interface ModuleAccessData { modules: ModuleDef[]; access: ModuleAccessRow[] }

// ─── Constants ────────────────────────────────────────────────────────────────

function decodeRole(token: string): string {
  try {
    const p = JSON.parse(atob(token.split(".")[1]!)) as Record<string, unknown>;
    return String(p["role"] ?? "");
  } catch { return ""; }
}

const ALL_ROLES: string[] = ASSIGNABLE_ROLES;
const VIEW_OPTS  = ["HIDDEN", "MASKED", "FULL"];
const EDIT_OPTS  = ["NONE", "DIRECT", "WITH_APPROVAL"];
const SENS_OPTS  = ["PUBLIC", "INTERNAL", "CONFIDENTIAL", "RESTRICTED"];
const STATUS_OPTS = ["ONBOARDING", "ACTIVE", "ON_LEAVE", "NOTICE_PERIOD", "TERMINATED"];

const inp: React.CSSProperties = {
  padding: "6px 8px", border: "1px solid var(--color-surface-200)",
  borderRadius: "var(--radius-sm)", fontSize: "12px", background: "var(--color-surface-0)",
  outline: "none",
};

const sensColor = (s: string): React.CSSProperties => ({
  fontSize: "10px", padding: "1px 6px", borderRadius: 999, fontWeight: 600,
  background: s === "PUBLIC" ? "var(--color-success-bg)" : s === "INTERNAL" ? "var(--color-info-bg)" : s === "CONFIDENTIAL" ? "var(--color-warning-bg)" : "var(--color-error-bg)",
  color:      s === "PUBLIC" ? "var(--color-success-fg)" : s === "INTERNAL" ? "var(--color-info-fg)" : s === "CONFIDENTIAL" ? "var(--color-warning-fg)" : "var(--color-error-fg)",
});

// ─── Tab 1: Permission Matrix ─────────────────────────────────────────────────

function MatrixTab() {
  const [matrix, setMatrix]       = useState<PermMatrix | null>(null);
  const [selectedRole, setSelectedRole] = useState(ALL_ROLES[0]!);
  const [saving, setSaving]       = useState<string | null>(null);
  const [saveMsg, setSaveMsg]     = useState("");
  const [drafts, setDrafts]       = useState<Record<string, Partial<RolePermission>>>({});
  const [expanded, setExpanded]   = useState<Set<string>>(new Set());

  function toggleExpanded(field: string) {
    setExpanded((prev) => { const n = new Set(prev); if (n.has(field)) n.delete(field); else n.add(field); return n; });
  }

  const load = useCallback(async () => {
    try {
      const { data } = await api.get<PermMatrix>("/admin/permissions");
      setMatrix(data);
    } catch (err) { logError("FieldPermissionsPage.loadMatrix", err); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  function getPermForField(field: string): RolePermission {
    const existing = matrix?.permissions.find((p) => p.role === selectedRole && p.field === field);
    const draft = drafts[field];
    return {
      role: selectedRole, field,
      canViewInList:    draft?.canViewInList    ?? existing?.canViewInList    ?? "HIDDEN",
      canViewInProfile: draft?.canViewInProfile ?? existing?.canViewInProfile ?? "HIDDEN",
      canEdit:          draft?.canEdit          ?? existing?.canEdit          ?? "NONE",
      approverRoles:    draft?.approverRoles    ?? existing?.approverRoles    ?? [],
      lockedWhenStatus: draft?.lockedWhenStatus ?? existing?.lockedWhenStatus ?? [],
    };
  }

  function setDraft(field: string, patch: Partial<RolePermission>) {
    setDrafts((prev) => ({ ...prev, [field]: { ...prev[field], ...patch } }));
  }

  async function saveField(field: string) {
    const perm = getPermForField(field);
    const isRestricted = matrix?.fields.find((f) => f.field === field)?.sensitivity === "RESTRICTED";
    if (isRestricted && (perm.canViewInProfile === "FULL" || perm.canViewInList === "FULL")) {
      if (!confirm(`Granting FULL access to a RESTRICTED field (${field}) for role ${selectedRole}. Are you sure?`)) return;
    }
    setSaving(field);
    setSaveMsg("");
    try {
      await api.put(`/admin/permissions/${selectedRole}/${field}`, {
        canViewInList:    perm.canViewInList,
        canViewInProfile: perm.canViewInProfile,
        canEdit:          perm.canEdit,
        approverRoles:    perm.approverRoles,
        lockedWhenStatus: perm.lockedWhenStatus,
      });
      setDrafts((prev) => { const n = { ...prev }; delete n[field]; return n; });
      setSaveMsg(`Saved ${field}.`);
      setTimeout(() => setSaveMsg(""), 2000);
      void load();
    } catch (err) { logError("FieldPermissionsPage.save", err); setSaveMsg("Save failed."); }
    setSaving(null);
  }

  const sectionGroups: Record<string, FieldDef[]> = {};
  for (const f of matrix?.fields ?? []) {
    (sectionGroups[f.section] ??= []).push(f);
  }

  return (
    <div>
      {/* Role selector */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
        <label style={{ fontSize: "13px", fontWeight: 600, color: "var(--color-surface-700)" }}>Editing permissions for role:</label>
        <SelectField size="sm" specular value={selectedRole} onChange={(e) => { setSelectedRole(e.target.value); setDrafts({}); }}>
          {ALL_ROLES.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
        </SelectField>
        {saveMsg && <span style={{ fontSize: "12px", color: saveMsg.includes("failed") ? "var(--color-error-fg)" : "var(--color-success-fg)" }}>{saveMsg}</span>}
      </div>

      {/* Column headers */}
      <div style={{
        display: "grid", gridTemplateColumns: "1fr 110px 120px 130px 28px 80px",
        gap: "8px", padding: "6px 10px",
        background: "var(--color-surface-50)", borderRadius: "var(--radius-sm)",
        marginBottom: "6px", fontSize: "11px", fontWeight: 700, color: "var(--color-surface-600)",
        textTransform: "uppercase", letterSpacing: "0.4px",
      }}>
        <span>Field</span>
        <span>List View</span>
        <span>Profile View</span>
        <span>Edit Mode</span>
        <span></span>
        <span></span>
      </div>

      {Object.entries(sectionGroups).map(([section, fields]) => (
        <div key={section} style={{ marginBottom: "20px" }}>
          <p style={{ margin: "0 0 6px", fontSize: "11px", fontWeight: 700, color: "var(--color-surface-500)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            {section}
          </p>
          {fields.map((fd) => {
            const perm = getPermForField(fd.field);
            const isDirty = !!drafts[fd.field];
            const isExpanded = expanded.has(fd.field);
            return (
              <div key={fd.field} style={{ borderBottom: "1px solid var(--color-surface-100)", background: isDirty ? "var(--color-warning-bg-subtle)" : "var(--color-surface-0)" }}>
                {/* Main row */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 110px 120px 130px 28px 80px", gap: "8px", padding: "7px 10px", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ fontSize: "13px", color: "var(--color-surface-800)" }}>{fd.label}</span>
                    <span style={sensColor(fd.sensitivity)}>{fd.sensitivity}</span>
                  </div>
                  <SelectField size="sm" value={perm.canViewInList} onChange={(e) => setDraft(fd.field, { canViewInList: e.target.value })}>
                    {VIEW_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </SelectField>
                  <SelectField size="sm" value={perm.canViewInProfile} onChange={(e) => setDraft(fd.field, { canViewInProfile: e.target.value })}>
                    {VIEW_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </SelectField>
                  <SelectField size="sm" value={perm.canEdit} onChange={(e) => setDraft(fd.field, { canEdit: e.target.value })}>
                    {EDIT_OPTS.map((o) => <option key={o} value={o}>{o === "WITH_APPROVAL" ? "Needs Approval" : o}</option>)}
                  </SelectField>
                  <button
                    onClick={() => toggleExpanded(fd.field)}
                    title={isExpanded ? "Collapse advanced" : "Approver roles & status locks"}
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: "14px", color: isExpanded ? "var(--color-brand-600)" : "var(--color-surface-400)", padding: "2px" }}
                  >
                    {isExpanded ? "▲" : "⋯"}
                  </button>
                  <button
                    disabled={!isDirty || saving === fd.field}
                    onClick={() => void saveField(fd.field)}
                    style={{
                      padding: "5px 10px", background: isDirty ? "var(--color-brand-600)" : "var(--color-surface-100)",
                      color: isDirty ? "#fff" : "var(--color-surface-400)",
                      border: "none", borderRadius: "var(--radius-sm)", cursor: isDirty ? "pointer" : "default",
                      fontSize: "12px", fontWeight: 600,
                    }}
                  >
                    {saving === fd.field ? "…" : "Save"}
                  </button>
                </div>

                {/* Advanced panel */}
                {isExpanded && (
                  <div style={{ padding: "12px 14px 16px 14px", borderTop: "1px solid var(--color-surface-100)", background: "var(--color-surface-50)", display: "flex", gap: "32px" }}>
                    {/* Approver Roles */}
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: "0 0 8px", fontSize: "11px", fontWeight: 700, color: "var(--color-surface-600)", textTransform: "uppercase", letterSpacing: "0.4px" }}>
                        Approver Roles
                        {perm.canEdit !== "WITH_APPROVAL" && <span style={{ fontWeight: 400, textTransform: "none", marginLeft: "6px", color: "var(--color-surface-400)" }}>(applies when Edit = Needs Approval)</span>}
                      </p>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                        {ALL_ROLES.map((r) => (
                          <button
                            key={r}
                            onClick={() => {
                              const cur = perm.approverRoles;
                              setDraft(fd.field, { approverRoles: cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r] });
                            }}
                            style={{
                              padding: "3px 8px", fontSize: "11px", borderRadius: 999, cursor: "pointer",
                              border: `1px solid ${perm.approverRoles.includes(r) ? "var(--color-brand-400)" : "var(--color-surface-200)"}`,
                              background: perm.approverRoles.includes(r) ? "var(--color-brand-50)" : "var(--color-surface-0)",
                              color: perm.approverRoles.includes(r) ? "var(--color-brand-700)" : "var(--color-surface-500)",
                              fontWeight: perm.approverRoles.includes(r) ? 600 : 400,
                            }}
                          >
                            {r.replace(/_/g, " ")}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Lock When Status */}
                    <div>
                      <p style={{ margin: "0 0 8px", fontSize: "11px", fontWeight: 700, color: "var(--color-surface-600)", textTransform: "uppercase", letterSpacing: "0.4px" }}>
                        Lock When Status
                      </p>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                        {STATUS_OPTS.map((s) => (
                          <button
                            key={s}
                            onClick={() => {
                              const cur = perm.lockedWhenStatus;
                              setDraft(fd.field, { lockedWhenStatus: cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s] });
                            }}
                            style={{
                              padding: "3px 8px", fontSize: "11px", borderRadius: 999, cursor: "pointer",
                              border: `1px solid ${perm.lockedWhenStatus.includes(s) ? "var(--color-error-border)" : "var(--color-surface-200)"}`,
                              background: perm.lockedWhenStatus.includes(s) ? "var(--color-error-bg-subtle)" : "var(--color-surface-0)",
                              color: perm.lockedWhenStatus.includes(s) ? "var(--color-error-fg)" : "var(--color-surface-500)",
                              fontWeight: perm.lockedWhenStatus.includes(s) ? 600 : 400,
                            }}
                          >
                            {s.replace(/_/g, " ")}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ─── Tab 2: Temporary Grants ──────────────────────────────────────────────────

function TempGrantsTab({ fields }: { fields: FieldDef[] }) {
  const [grants, setGrants]     = useState<TempGrant[]>([]);
  const [loading, setLoading]   = useState(true);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [users, setUsers]       = useState<UserOption[]>([]);
  const [form, setForm]         = useState({
    userId: "", field: "", canViewInList: "FULL", canViewInProfile: "FULL", canEdit: "NONE",
    reason: "", expiresAt: "",
  });
  const [formErr, setFormErr]   = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<TempGrant[]>("/admin/temporary-grants");
      setGrants(data);
    } catch (err) { logError("FieldPermissionsPage.loadGrants", err); }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
    api.get<{ data: UserOption[] }>("/employees?limit=200")
      .then(({ data }) => setUsers(data.data))
      .catch((err) => logError("FieldPermissionsPage.loadUsers", err));
  }, [load]);

  async function revoke(id: string) {
    setRevoking(id);
    try {
      await api.delete(`/admin/temporary-grants/${id}`);
      void load();
    } catch (err) { logError("FieldPermissionsPage.revokeGrant", err, { grantId: id }); }
    setRevoking(null);
  }

  async function submit() {
    if (!form.userId || !form.field || !form.reason.trim() || !form.expiresAt) {
      setFormErr("User, field, reason, and expiry are all required."); throw new Error("validation");
    }
    if (new Date(form.expiresAt) <= new Date()) {
      setFormErr("Expiry date must be in the future."); throw new Error("validation");
    }
    setSubmitting(true); setFormErr("");
    try {
      await api.post("/admin/temporary-grants", form);
      setForm({ userId: "", field: "", canViewInList: "FULL", canViewInProfile: "FULL", canEdit: "NONE", reason: "", expiresAt: "" });
      setShowForm(false);
      void load();
    } catch (err) {
      logError("FieldPermissionsPage.submitGrant", err);
      setFormErr("Failed to create grant.");
      throw err;
    } finally { setSubmitting(false); }
  }

  const active = grants.filter((g) => !g.revoked && new Date(g.expiresAt) > new Date());

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <p style={{ margin: 0, fontSize: "13px", color: "var(--color-surface-700)" }}>
          {active.length} active grant{active.length !== 1 ? "s" : ""}
        </p>
        <button onClick={() => setShowForm((v) => !v)} style={{ padding: "7px 14px", background: "var(--color-brand-600)", color: "#fff", border: "none", borderRadius: "var(--radius-md)", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}>
          {showForm ? "Cancel" : "+ New Grant"}
        </button>
      </div>

      {showForm && (
        <div style={{ background: "var(--color-surface-50)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: "16px", marginBottom: "16px" }}>
          <p style={{ margin: "0 0 12px", fontSize: "14px", fontWeight: 700 }}>New Temporary Grant</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
            <div>
              <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "3px" }}>Employee *</label>
              <SelectField size="sm" value={form.userId} onChange={(e) => setForm((p) => ({ ...p, userId: e.target.value }))}>
                <option value="">Select employee…</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.employee ? `${u.employee.firstName} ${u.employee.lastName}` : u.email}
                  </option>
                ))}
              </SelectField>
            </div>
            <div>
              <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "3px" }}>Field *</label>
              <SelectField size="sm" value={form.field} onChange={(e) => setForm((p) => ({ ...p, field: e.target.value }))}>
                <option value="">Select field…</option>
                {fields.map((f) => <option key={f.field} value={f.field}>{f.label} ({f.section})</option>)}
              </SelectField>
            </div>
            <div>
              <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "3px" }}>List View Access</label>
              <SelectField size="sm" value={form.canViewInList} onChange={(e) => setForm((p) => ({ ...p, canViewInList: e.target.value }))}>
                {VIEW_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
              </SelectField>
            </div>
            <div>
              <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "3px" }}>Profile View Access</label>
              <SelectField size="sm" value={form.canViewInProfile} onChange={(e) => setForm((p) => ({ ...p, canViewInProfile: e.target.value }))}>
                {VIEW_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
              </SelectField>
            </div>
            <div>
              <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "3px" }}>Edit Access</label>
              <SelectField size="sm" value={form.canEdit} onChange={(e) => setForm((p) => ({ ...p, canEdit: e.target.value }))}>
                {EDIT_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
              </SelectField>
            </div>
            <div>
              <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "3px" }}>Expires At *</label>
              <input type="datetime-local" value={form.expiresAt} onChange={(e) => setForm((p) => ({ ...p, expiresAt: e.target.value }))} style={{ ...inp, width: "100%" }} />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "3px" }}>Reason * (shown in audit log)</label>
              <input value={form.reason} onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))} placeholder="e.g. Payroll reconciliation audit — expires end of month" style={{ ...inp, width: "100%", boxSizing: "border-box" }} maxLength={280} />
            </div>
          </div>
          {formErr && <p style={{ margin: "8px 0 0", fontSize: "12px", color: "var(--color-error-fg)" }}>{formErr}</p>}
          <AsyncButton variant="success" disabled={submitting} onClick={() => submit()} className="mt-3">
            Create Grant
          </AsyncButton>
        </div>
      )}

      {loading ? (
        <p style={{ fontSize: "13px", color: "var(--color-surface-500)" }}>Loading…</p>
      ) : grants.length === 0 ? (
        <p style={{ fontSize: "13px", color: "var(--color-surface-400)" }}>No grants yet.</p>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead>
            <tr style={{ background: "var(--color-surface-50)", borderBottom: "1px solid var(--color-surface-200)" }}>
              {["Employee", "Field", "List / Profile / Edit", "Reason", "Expires", "Status", ""].map((h) => (
                <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: "11px", fontWeight: 700, color: "var(--color-surface-600)", textTransform: "uppercase", letterSpacing: "0.4px" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grants.map((g, i) => {
              const expired = new Date(g.expiresAt) <= new Date();
              const isActive = !g.revoked && !expired;
              const ttl = expired ? "Expired" : `Expires ${new Date(g.expiresAt).toLocaleDateString("en-IN")}`;
              return (
                <tr key={g.id} style={{ borderBottom: i < grants.length - 1 ? "1px solid var(--color-surface-100)" : "none", opacity: isActive ? 1 : 0.5 }}>
                  <td style={{ padding: "10px 12px" }}>
                    <div style={{ fontWeight: 600 }}>
                      {g.user.employee ? `${g.user.employee.firstName} ${g.user.employee.lastName}` : g.user.email}
                    </div>
                    <div style={{ fontSize: "11px", color: "var(--color-surface-500)" }}>{g.user.email}</div>
                  </td>
                  <td style={{ padding: "10px 12px", fontFamily: "var(--font-mono)", fontSize: "12px" }}>{g.field}</td>
                  <td style={{ padding: "10px 12px", fontSize: "12px", color: "var(--color-surface-700)" }}>
                    {g.canViewInList} / {g.canViewInProfile} / {g.canEdit}
                  </td>
                  <td style={{ padding: "10px 12px", fontSize: "12px", color: "var(--color-surface-600)", maxWidth: "200px" }}>{g.reason}</td>
                  <td style={{ padding: "10px 12px", fontSize: "11px", color: expired ? "var(--color-error-fg)" : "var(--color-surface-600)", whiteSpace: "nowrap" }}>{ttl}</td>
                  <td style={{ padding: "10px 12px" }}>
                    <span style={{ fontSize: "10px", padding: "2px 6px", borderRadius: 999, fontWeight: 600, background: isActive ? "var(--color-success-bg)" : "var(--color-surface-100)", color: isActive ? "var(--color-success-fg)" : "var(--color-text-secondary)" }}>
                      {g.revoked ? "Revoked" : expired ? "Expired" : "Active"}
                    </span>
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    {isActive && (
                      <button disabled={revoking === g.id} onClick={() => void revoke(g.id)} style={{ padding: "4px 10px", background: "var(--color-error-bg)", color: "var(--color-error-fg)", border: "1px solid var(--color-error-border)", borderRadius: "var(--radius-sm)", cursor: "pointer", fontSize: "11px", fontWeight: 600 }}>
                        {revoking === g.id ? "…" : "Revoke"}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Tab 3: Field Registry ────────────────────────────────────────────────────

function FieldRegistryTab() {
  const [fields, setFields]   = useState<FieldDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState<string | null>(null);
  const [drafts, setDrafts]   = useState<Record<string, Partial<FieldDef>>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<FieldDef[]>("/admin/field-registry");
      setFields(data);
    } catch (err) { logError("FieldPermissionsPage.loadRegistry", err); }
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  function setDraft(field: string, patch: Partial<FieldDef>) {
    setDrafts((prev) => ({ ...prev, [field]: { ...prev[field], ...patch } }));
  }

  async function saveField(field: string) {
    const current = fields.find((f) => f.field === field)!;
    const draft = drafts[field] ?? {};
    setSaving(field);
    try {
      await api.patch(`/admin/field-registry/${field}`, {
        label:       draft.label       ?? current.label,
        sensitivity: draft.sensitivity ?? current.sensitivity,
      });
      setDrafts((prev) => { const n = { ...prev }; delete n[field]; return n; });
      void load();
    } catch (err) { logError("FieldPermissionsPage.saveField", err, { field }); }
    setSaving(null);
  }

  const sectionGroups: Record<string, FieldDef[]> = {};
  for (const f of fields) {
    (sectionGroups[f.section] ??= []).push(f);
  }

  if (loading) return <p style={{ fontSize: "13px", color: "var(--color-surface-500)" }}>Loading…</p>;

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "160px 1fr 120px 140px 70px", gap: "8px", padding: "6px 10px", background: "var(--color-surface-50)", borderRadius: "var(--radius-sm)", marginBottom: "6px", fontSize: "11px", fontWeight: 700, color: "var(--color-surface-600)", textTransform: "uppercase", letterSpacing: "0.4px" }}>
        <span>Field Key</span><span>Label</span><span>Section</span><span>Sensitivity</span><span></span>
      </div>
      {Object.entries(sectionGroups).map(([section, fds]) => (
        <div key={section} style={{ marginBottom: "18px" }}>
          <p style={{ margin: "0 0 4px", fontSize: "11px", fontWeight: 700, color: "var(--color-surface-500)", textTransform: "uppercase", letterSpacing: "0.5px" }}>{section}</p>
          {fds.map((fd) => {
            const draft = drafts[fd.field] ?? {};
            const isDirty = !!drafts[fd.field];
            return (
              <div key={fd.field} style={{ display: "grid", gridTemplateColumns: "160px 1fr 120px 140px 70px", gap: "8px", padding: "6px 10px", alignItems: "center", borderBottom: "1px solid var(--color-surface-100)", background: isDirty ? "var(--color-warning-bg-subtle)" : "var(--color-surface-0)" }}>
                <span style={{ fontSize: "12px", fontFamily: "var(--font-mono)", color: "var(--color-surface-600)" }}>{fd.field}</span>
                <input
                  value={draft.label ?? fd.label}
                  onChange={(e) => setDraft(fd.field, { label: e.target.value })}
                  style={{ ...inp, width: "100%", boxSizing: "border-box" }}
                />
                <span style={{ fontSize: "12px", color: "var(--color-surface-600)" }}>{fd.section}</span>
                <SelectField size="sm"
                  value={draft.sensitivity ?? fd.sensitivity}
                  onChange={(e) => setDraft(fd.field, { sensitivity: e.target.value })}
                >
                  {SENS_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
                </SelectField>
                <button
                  disabled={!isDirty || saving === fd.field}
                  onClick={() => void saveField(fd.field)}
                  style={{ padding: "4px 8px", background: isDirty ? "var(--color-brand-600)" : "var(--color-surface-100)", color: isDirty ? "#fff" : "var(--color-surface-400)", border: "none", borderRadius: "var(--radius-sm)", cursor: isDirty ? "pointer" : "default", fontSize: "11px", fontWeight: 600 }}
                >
                  {saving === fd.field ? "…" : "Save"}
                </button>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ─── Tab 4: Module Access ───────────────────────────────────────────────────────

function ModuleAccessTab() {
  const [data, setData]                 = useState<ModuleAccessData | null>(null);
  const [selectedRole, setSelectedRole] = useState(ALL_ROLES[0]!);
  const [saving, setSaving]             = useState<string | null>(null);
  const [saveMsg, setSaveMsg]           = useState("");
  const [drafts, setDrafts]             = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get<ModuleAccessData>("/admin/module-access");
      setData(data);
    } catch (err) { logError("FieldPermissionsPage.loadModuleAccess", err); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  function getCanView(moduleKey: string): boolean {
    if (moduleKey in drafts) return drafts[moduleKey]!;
    const existing = data?.access.find((a) => a.role === selectedRole && a.moduleKey === moduleKey);
    return existing?.canView ?? false;
  }

  function setDraft(moduleKey: string, canView: boolean) {
    setDrafts((prev) => ({ ...prev, [moduleKey]: canView }));
  }

  async function saveModule(moduleKey: string) {
    setSaving(moduleKey);
    setSaveMsg("");
    try {
      await api.put(`/admin/module-access/${selectedRole}/${moduleKey}`, { canView: getCanView(moduleKey) });
      setDrafts((prev) => { const n = { ...prev }; delete n[moduleKey]; return n; });
      setSaveMsg(`Saved ${moduleKey}.`);
      setTimeout(() => setSaveMsg(""), 2000);
      void load();
    } catch (err) { logError("FieldPermissionsPage.saveModuleAccess", err); setSaveMsg("Save failed."); }
    setSaving(null);
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
        <label style={{ fontSize: "13px", fontWeight: 600, color: "var(--color-surface-700)" }}>Editing module access for role:</label>
        <SelectField size="sm" specular value={selectedRole} onChange={(e) => { setSelectedRole(e.target.value); setDrafts({}); }}>
          {ALL_ROLES.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
        </SelectField>
        {saveMsg && <span style={{ fontSize: "12px", color: saveMsg.includes("failed") ? "var(--color-error-fg)" : "var(--color-success-fg)" }}>{saveMsg}</span>}
      </div>

      <div style={{
        display: "grid", gridTemplateColumns: "1fr 140px 90px",
        gap: "8px", padding: "6px 10px",
        background: "var(--color-surface-50)", borderRadius: "var(--radius-sm)",
        marginBottom: "6px", fontSize: "11px", fontWeight: 700, color: "var(--color-surface-600)",
        textTransform: "uppercase", letterSpacing: "0.4px",
      }}>
        <span>Module</span>
        <span>Visible in Nav</span>
        <span></span>
      </div>

      {(data?.modules ?? []).map((m) => {
        const canView = getCanView(m.key);
        const isDirty = m.key in drafts;
        return (
          <div key={m.key} style={{
            display: "grid", gridTemplateColumns: "1fr 140px 90px", gap: "8px", padding: "7px 10px",
            alignItems: "center", borderBottom: "1px solid var(--color-surface-100)",
            background: isDirty ? "var(--color-warning-bg-subtle)" : "var(--color-surface-0)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "13px", color: "var(--color-surface-800)" }}>{m.label}</span>
              <span style={{ fontSize: "11px", color: "var(--color-surface-400)" }}>{m.routePrefix}</span>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
              <input type="checkbox" checked={canView} onChange={(e) => setDraft(m.key, e.target.checked)} />
              <span style={{ fontSize: "12px", color: "var(--color-surface-600)" }}>{canView ? "Visible" : "Hidden"}</span>
            </label>
            <button
              disabled={!isDirty || saving === m.key}
              onClick={() => void saveModule(m.key)}
              style={{
                padding: "5px 10px", background: isDirty ? "var(--color-brand-600)" : "var(--color-surface-100)",
                color: isDirty ? "#fff" : "var(--color-surface-400)",
                border: "none", borderRadius: "var(--radius-sm)", cursor: isDirty ? "pointer" : "default",
                fontSize: "12px", fontWeight: 600,
              }}
            >
              {saving === m.key ? "…" : "Save"}
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function FieldPermissionsPage() {
  usePageBack("/admin", "Back to Admin");
  const { accessToken } = useAuth();
  const role = accessToken ? decodeRole(accessToken) : "";
  const [tab, setTab] = useState<"matrix" | "grants" | "registry" | "moduleAccess">("matrix");
  const [fields, setFields] = useState<FieldDef[]>([]);

  useEffect(() => {
    api.get<FieldDef[]>("/admin/field-registry").then(({ data }) => setFields(data)).catch((err) => logError("FieldPermissionsPage.loadRegistry", err));
  }, []);

  if (!ADMIN_ROLES.has(role)) {
    return (
      <div style={{ padding: "var(--space-8)" }}>
        <p style={{ color: "var(--color-surface-700)" }}>You don't have access to this page.</p>
      </div>
    );
  }

  const TAB: (t: typeof tab) => React.CSSProperties = (t) => ({
    padding: "8px 18px", border: "none", background: "none", cursor: "pointer",
    fontSize: "13px", fontWeight: tab === t ? 700 : 400,
    color: tab === t ? "var(--color-brand-700)" : "var(--color-surface-700)",
    borderBottom: tab === t ? "2px solid var(--color-brand-600)" : "2px solid transparent",
  });

  return (
    <div style={{ minHeight: "100vh", background: "var(--color-surface-50)" }}>
      {/* Header */}
      <div style={{
        background: "var(--color-surface-0)", borderBottom: "1px solid var(--color-surface-200)",
        padding: "var(--space-5) var(--space-8)", display: "flex", alignItems: "center", gap: "12px",
      }}>
        <h1 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "var(--color-surface-900)" }}>Field Permissions</h1>
      </div>

      {/* Tabs */}
      <div style={{ background: "var(--color-surface-0)", borderBottom: "1px solid var(--color-surface-200)", display: "flex", padding: "0 var(--space-8)" }}>
        <button style={TAB("matrix")} onClick={() => setTab("matrix")}>Permission Matrix</button>
        <button style={TAB("grants")} onClick={() => setTab("grants")}>Temporary Grants</button>
        <button style={TAB("registry")} onClick={() => setTab("registry")}>Field Registry</button>
        <button style={TAB("moduleAccess")} onClick={() => setTab("moduleAccess")}>Module Access</button>
      </div>

      <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "var(--space-6) var(--space-8)" }}>
        {tab === "matrix"       && <MatrixTab />}
        {tab === "grants"       && <TempGrantsTab fields={fields} />}
        {tab === "registry"     && <FieldRegistryTab />}
        {tab === "moduleAccess" && <ModuleAccessTab />}
      </div>
    </div>
  );
}
